import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Mail, MapPin, Clock, Award, Home, Ruler } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const FloorPlansExeter = () => {
  const faqs = [
    {
      question: "How much do floor plans cost in Exeter?",
      answer: "Floor plan pricing in Exeter starts from £80 for a standard 1-2 bedroom property. Prices vary based on property size and complexity. A typical 3-bedroom Exeter house costs £100-£120, while larger properties (4-5 bedrooms) range from £140-£180. We offer discounts for estate agents and landlords with multiple Exeter properties."
    },
    {
      question: "How quickly can you produce floor plans for my Exeter property?",
      answer: "We typically measure properties within 2-3 working days across all Exeter postcodes. Once measured, your professional floor plans are delivered within 48 hours in multiple formats (PDF, JPG, PNG). For urgent Exeter property listings, we can offer same-day measurement and 24-hour delivery - call 01392 980077 to discuss."
    },
    {
      question: "What format will I receive my Exeter floor plans in?",
      answer: "All Exeter floor plans are delivered in high-resolution PDF, JPG, and PNG formats suitable for Rightmove, Zoopla, OnTheMarket, property brochures, and print marketing. Plans are drawn to scale and include room dimensions, total floor area (sq ft and sq m), and a professional layout suitable for estate agency use."
    },
    {
      question: "Do your floor plans meet Rightmove and Zoopla standards?",
      answer: "Yes, all our Exeter floor plans are drawn to professional standards compliant with Rightmove, Zoopla, OnTheMarket, and other major property portals. Plans include accurate room dimensions, north point orientation, and clear labeling suitable for marketing Exeter properties to buyers and tenants."
    },
    {
      question: "Can you combine floor plans with EPC assessments in Exeter?",
      answer: "Absolutely. Many Exeter estate agents and landlords book both services together for efficiency and cost savings. We can measure your property for floor plans and conduct the EPC assessment in a single visit, saving you time and potentially reducing costs. Contact us for a combined quote."
    }
  ];

  const exeterAreas = [
    "EX1 - Exeter City Centre, Cathedral Quarter, Quayside",
    "EX2 - St Thomas, Alphington, Ide, Shillingford",
    "EX4 - Heavitree, Whipton, Pinhoe, Polsloe",
    "EX5 - Clyst St Mary, Topsham (nearby)"
  ];

  return (
    <>
      <Helmet>
        <title>Floor Plans Exeter | Professional Property Floor Plans | From £80 | Fast Delivery</title>
        <meta name="description" content="Professional floor plans for Exeter properties from £80. Fast 48-hour delivery. Rightmove & Zoopla compliant. Covering all EX postcodes. Book your Exeter floor plan today - 01392 980077." />
        <meta name="keywords" content="floor plans Exeter, property floor plans Exeter, estate agent floor plans Exeter, Rightmove floor plans, measured floor plans Exeter, EX1 floor plans, EX2 floor plans, property marketing Exeter, floor plan cost Exeter" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/floor-plans-exeter" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Professional Floor Plans for Property Marketing",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Energy Performance Direct",
              "telephone": "+44-1392-980077",
              "email": "info@energyperformancedirect.co.uk",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Exeter",
                "addressRegion": "Devon",
                "postalCode": "EX1",
                "addressCountry": "GB"
              }
            },
            "areaServed": {
              "@type": "City",
              "name": "Exeter"
            },
            "offers": {
              "@type": "Offer",
              "price": "80",
              "priceCurrency": "GBP",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "minPrice": "80"
              }
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Estate Agents, Landlords, Property Sellers in Exeter"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-green-50 via-cyan-50 to-green-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">Covering All Exeter Postcodes</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                Professional Floor Plans in Exeter
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                High-quality measured floor plans for Exeter properties from £80. Rightmove & Zoopla compliant. Fast 48-hour delivery. Covering EX1, EX2, EX4 & EX5.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link to="/booking">
                  <Button className="text-white hover:opacity-90 px-8 py-6 text-lg rounded-full shadow-lg" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
                    Book Exeter Floor Plan
                  </Button>
                </Link>
                <a href="tel:01392980077">
                  <Button variant="outline" className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Ruler className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">Accurate Measurement</h3>
                  <p className="text-sm text-gray-600">Laser-measured to professional standards</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Clock className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">48-Hour Delivery</h3>
                  <p className="text-sm text-gray-600">Fast turnaround for urgent listings</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <MapPin className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">Local to Exeter</h3>
                  <p className="text-sm text-gray-600">Based in Exeter since 2009</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Home className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">From £80</h3>
                  <p className="text-sm text-gray-600">Competitive pricing for Exeter properties</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Floor Plan Services in Exeter</h2>
              
              <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
                <p>
                  Energy Performance Direct provides professional measured floor plans for residential and commercial properties throughout Exeter and the surrounding areas. Operating in Exeter since 2009, we have extensive experience surveying Exeter's diverse property types—from Victorian terraces in St Thomas to modern apartments in the city centre and executive homes in Pennsylvania.
                </p>

                <p>
                  Professional floor plans are essential for effective property marketing in Exeter. Whether you're an estate agent preparing a property for Rightmove, a landlord marketing a rental on Zoopla, or a homeowner selling privately, a clear, accurate floor plan helps potential buyers and tenants visualize the property layout, understand room flow, and assess space before arranging a viewing. Our Exeter floor plans are drawn to professional standards, fully compliant with major property portals, and delivered in multiple digital formats ready for immediate use.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Why Choose Energy Performance Direct for Exeter Floor Plans?</h3>

                <p>
                  As a local Exeter business with over 15 years' experience, we understand the Exeter property market and the specific requirements of local estate agents, landlords, and homeowners. All our floor plans are laser-measured to ensure accuracy, drawn to scale with clear room dimensions, and presented in a clean, professional format that enhances your property marketing materials.
                </p>

                <p>
                  We have surveyed hundreds of Exeter properties since 2009, from compact city-centre flats to large family homes in Topsham and Alphington. Our local knowledge means we can often arrange same-day or next-day measurement visits across Exeter postcodes, with your finished floor plans delivered within 48 hours—perfect for time-sensitive property listings.
                </p>

                <div className="bg-green-50 border-l-4 border-green-600 p-6 my-8">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">What Exeter Estate Agents Say:</h4>
                  <p className="text-gray-700 italic mb-2">
                    "We've used Energy Performance Direct for floor plans on many occasions. The plans are delivered speedily and are always accurate and professional. Excellent service."
                  </p>
                  <p className="text-sm text-gray-600">— Exeter Estate Agent (FreeIndex Review)</p>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Exeter Areas We Cover for Floor Plans</h3>

                <p>
                  We provide professional floor plan services across all Exeter postcodes with no or minimal travel surcharge for properties within the Exeter urban area. Our surveyors know Exeter well and can navigate efficiently to properties in all areas, from the historic city centre to new developments on the Exeter periphery.
                </p>

                <div className="bg-white border border-gray-200 rounded-lg p-6 my-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">Exeter Postcodes Covered:</h4>
                  <ul className="space-y-2">
                    {exeterAreas.map((area, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{area}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-gray-600 mt-4">
                    We also cover nearby areas including Topsham, Exminster, Woodbury, Broadclyst, Pinhoe, and surrounding villages within the Exeter area.
                  </p>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">The Floor Plan Measurement Process</h3>

                <p>
                  Our Exeter floor plan service is straightforward and non-disruptive. A professional surveyor visits your property at a time convenient to you (typically within 2-3 working days of booking) and conducts a thorough measurement of all rooms using laser measurement equipment. The survey usually takes 30-60 minutes for a standard Exeter property and includes:
                </p>

                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Laser measurement of all internal rooms and spaces</li>
                  <li>Recording room names and usage (bedroom, living room, kitchen, etc.)</li>
                  <li>Noting fixed features (fitted wardrobes, stairs, bathrooms)</li>
                  <li>Measuring external dimensions where accessible</li>
                  <li>Photographing property layout for reference</li>
                  <li>Recording total floor area in square feet and square metres</li>
                </ul>

                <p>
                  Once the survey is complete, our CAD technicians draw your floor plan to professional standards with clear room labels, accurate dimensions, and a clean, modern aesthetic suitable for property marketing. Your floor plan is delivered within 48 hours in PDF, JPG, and PNG formats, ready to upload to Rightmove, Zoopla, OnTheMarket, or include in property brochures and marketing materials.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Floor Plan Pricing for Exeter Properties</h3>

                <p>
                  Our Exeter floor plan pricing is transparent and competitive. Standard residential floor plan pricing is as follows:
                </p>

                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>1-2 bedroom properties:</strong> £80-£100 (flats, maisonettes, small houses)</li>
                  <li><strong>3 bedroom properties:</strong> £100-£120 (typical Exeter family homes)</li>
                  <li><strong>4 bedroom properties:</strong> £140-£160 (larger detached/semi-detached homes)</li>
                  <li><strong>5+ bedroom properties:</strong> £160-£200+ (executive homes, period properties)</li>
                </ul>

                <p>
                  Properties in central Exeter (EX1, EX2, EX4) have no travel surcharge. Outlying areas (EX5, Topsham, Exminster) may have a small travel charge based on distance. We offer discounts for estate agents and landlords requiring multiple floor plans across Exeter properties. Contact us on 01392 980077 or email info@energyperformancedirect.co.uk for a tailored quote.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Floor Plans for Exeter Estate Agents</h3>

                <p>
                  Exeter estate agents rely on professional floor plans to differentiate their property listings and provide buyers with essential layout information before viewings. Our floor plans meet all Rightmove, Zoopla, and OnTheMarket technical specifications and are designed to showcase properties in the best light. We work with several established Exeter estate agencies and understand the fast-paced nature of property marketing in the Exeter area.
                </p>

                <p>
                  For estate agents with multiple properties requiring floor plans, we offer bulk pricing discounts and priority scheduling to ensure your Exeter listings are market-ready as quickly as possible. We can also provide combined floor plan and EPC services in a single property visit, saving time and reducing costs.
                </p>

                <div className="bg-cyan-50 border-l-4 border-cyan-600 p-6 my-8">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Need an EPC Too?</h4>
                  <p className="text-gray-700 mb-4">
                    Most Exeter property sales and lettings require both a floor plan and an EPC. We can measure your property for the floor plan and conduct the EPC assessment in the same visit, providing both services efficiently and at a competitive combined price. Learn more about our <Link to="/domestic-epc-exeter" className="text-cyan-600 hover:text-cyan-700 underline font-semibold">Domestic EPC services in Exeter</Link>.
                  </p>
                  <Link to="/domestic-epc-exeter">
                    <Button variant="outline" className="border-cyan-600 text-cyan-600 hover:bg-cyan-50">
                      View EPC Services
                    </Button>
                  </Link>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">What's Included in Your Exeter Floor Plan?</h3>

                <p>
                  All our Exeter floor plans include the following as standard:
                </p>

                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Accurate scale drawing of all rooms and spaces</li>
                  <li>Room dimensions clearly labeled (metric and imperial)</li>
                  <li>Total floor area (square feet and square metres)</li>
                  <li>Room names/usage clearly indicated</li>
                  <li>North point orientation</li>
                  <li>Professional clean layout suitable for marketing</li>
                  <li>Multiple file formats (PDF, JPG, PNG) for versatile use</li>
                  <li>High-resolution output suitable for print and digital use</li>
                </ul>

                <p>
                  Additional services available on request include 3D floor plans, external site plans, and measured elevations for planning applications or architectural projects. Contact us to discuss your specific requirements.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Book Your Exeter Floor Plan Today</h3>

                <p>
                  Ready to order a professional floor plan for your Exeter property? Use our <Link to="/booking" className="text-green-600 hover:text-green-700 underline font-semibold">online booking system</Link> to select your preferred measurement date and time, or call us on 01392 980077 to speak directly with our team. We typically offer appointments within 2-3 working days across all Exeter postcodes, with your finished floor plan delivered within 48 hours.
                </p>

                <p>
                  For urgent floor plans (property launches, tenant move-in dates), please call us to discuss expedited service. We understand the importance of fast turnaround for Exeter property marketing and will do our best to accommodate tight deadlines.
                </p>
              </div>

              {/* FAQ Section */}
              <div className="mt-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions - Exeter Floor Plans</h2>
                <div className="space-y-6">
                  {faqs.map((faq, idx) => (
                    <Card key={idx} className="border-none shadow-md">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                        <p className="text-gray-700">{faq.answer}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* CTA Section */}
              <div className="mt-16 rounded-2xl p-8 text-white text-center" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
                <h2 className="text-3xl font-bold mb-4">Ready to Order Your Exeter Floor Plan?</h2>
                <p className="text-lg mb-6">
                  Professional measured floor plans across all Exeter postcodes. Call 01392 980077 or book online now.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/booking">
                    <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">
                      Book Online Now
                    </Button>
                  </Link>
                  <a href="tel:01392980077">
                    <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-6 text-lg rounded-full">
                      <Phone className="w-5 h-5 mr-2" />
                      Call 01392 980077
                    </Button>
                  </a>
                </div>
              </div>

              {/* Related Services */}
              <div className="mt-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Services in Exeter</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="border-none shadow-md hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <h4 className="text-lg font-bold mb-2">Domestic EPC Exeter</h4>
                      <p className="text-sm text-gray-600 mb-4">Energy certificates for Exeter homes</p>
                      <Link to="/domestic-epc-exeter">
                        <Button variant="outline" className="w-full">Learn More</Button>
                      </Link>
                    </CardContent>
                  </Card>
                  <Card className="border-none shadow-md hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <h4 className="text-lg font-bold mb-2">Energy Advice Reports</h4>
                      <p className="text-sm text-gray-600 mb-4">MEES compliance for landlords</p>
                      <Link to="/services/domestic_advice">
                        <Button variant="outline" className="w-full">Learn More</Button>
                      </Link>
                    </CardContent>
                  </Card>
                  <Card className="border-none shadow-md hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <h4 className="text-lg font-bold mb-2">All Services</h4>
                      <p className="text-sm text-gray-600 mb-4">View our full service range</p>
                      <Link to="/services">
                        <Button variant="outline" className="w-full">View Services</Button>
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              </div>

            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default FloorPlansExeter;
